import { Helmet } from "react-helmet-async";

/**
 * REC-11 → REC-19 SEO Head — single helper that drives `<title>`,
 * description, canonical, Open Graph, Twitter Cards and (optionally)
 * JSON-LD structured data for every public-facing page.
 *
 * Usage:
 *   <Seo
 *     title="Care Support Worker — Birmingham | Rabah Concept"
 *     description="…"
 *     canonical="https://hire.rabahconcept.co.uk/jobs/care-support-worker-birmingham-ref"
 *     image="https://hire.rabahconcept.co.uk/og-jobs.jpg"
 *     jsonLd={{...}}
 *   />
 */

const SITE_NAME = "Rabah Concept Recruitment";
const DEFAULT_OG = "https://hire.rabahconcept.co.uk/rabah-logo.png";

export default function Seo({
  title,
  description,
  canonical,
  image = DEFAULT_OG,
  type = "website",
  jsonLd, // single object or array of objects
  noindex = false,
}) {
  const fullTitle = title?.length > 60
    ? title.slice(0, 57).trimEnd() + "…"
    : title;
  const blocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

/** Builds the canonical public URL for the current path. Uses the
 * `REACT_APP_PUBLIC_BASE_URL` env if set (production), otherwise falls
 * back to `window.location.origin`. */
export function publicUrl(path = "") {
  const base = (process.env.REACT_APP_PUBLIC_BASE_URL
    || (typeof window !== "undefined" ? window.location.origin : "")
    || "").replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return base + p;
}
